import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { voiceConnectProviders, voiceConnectDiscover, voiceConnectSave, voiceConnectUse } from "../lib/api";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

// "Connect an agent" on the Calls tab: pick the calling service, paste its API key, choose from the agents and
// numbers it finds, and the CRM starts using it.
export default function ConnectVoiceModal({ close, onConnected }) {
  const [providers, setProviders] = useState(null);
  const [active, setActive] = useState("");
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [found, setFound] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [number, setNumber] = useState("");
  const [manual, setManual] = useState({});
  const [busy, setBusy] = useState("");
  const [makeActive, setMakeActive] = useState(true);
  const load = () => voiceConnectProviders().then(r => {
    setProviders(r.providers); setActive(r.active);
    setProvider(cur => cur || r.providers.find(x => !x.saved)?.key || r.providers[0]?.key || "");
  });
  useEffect(() => {
    load().catch(() => { toast.error("Couldn't load the calling services"); close(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const use = async (k) => {
    setBusy(`use-${k}`);
    try { const r = await voiceConnectUse(k); toast.success(`Calls now go through ${r.label}`); onConnected?.(); await load(); }
    catch (e) { toast.error(errText(e, "Couldn't switch")); }
    finally { setBusy(""); }
  };
  const p = providers?.find(x => x.key === provider);
  const pick = (k) => { setProvider(k); setFound(null); setAgentId(""); setNumber(""); setManual({}); };

  const look = async () => {
    setBusy("look");
    try {
      const r = await voiceConnectDiscover(provider, apiKey);
      setFound(r);
      if (r.agents.length === 1) setAgentId(r.agents[0].id);
      if (!r.agents.length && p.has_agents && provider !== "vapi") toast.message("Key accepted, but no agents were found. Create one in the service's dashboard first.");
    } catch (e) { toast.error(errText(e, "Couldn't connect")); }
    finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await voiceConnectSave(provider, { api_key: apiKey, agent_id: agentId, number, manual, make_active: makeActive }); toast.success(`${r.label} added${r.active ? " and in use" : ""}`); onConnected?.(); close(); }
    catch (e) { toast.error(errText(e, "Couldn't save")); }
    finally { setBusy(""); }
  };
  const needsAgent = p?.has_agents && provider !== "vapi";
  const canSave = apiKey.trim() && (!needsAgent || agentId) && (!p?.manual?.length || p.manual.every(([k]) => (manual[k] || "").trim())) && (provider !== "sarvam" || number.trim());

  return <div className="modal-backdrop" onClick={close}>
    <div className="modal login-card setup-card" onClick={e => e.stopPropagation()} data-testid="connect-voice-modal">
      <button className="modal-close" onClick={close}><X size={16} /></button>
      <h2>Calling services</h2>
      {!providers ? <p className="muted">Loading…</p> : <>
        {providers.some(x => x.saved) && <div className="connected-list">
          <b>Connected</b>
          {providers.filter(x => x.saved).map(x => <div key={x.key} className="source-card import-source">
            <div><b>{x.label}</b><small>{x.key === active ? "In use for calls" : "Connected — key saved"}</small></div>
            {x.key === active ? <span className="badge badge-green">In use</span>
              : <button className="outline-button" onClick={() => use(x.key)} disabled={!!busy}>{busy === `use-${x.key}` ? <Loader2 size={14} className="spin" /> : "Use for calls"}</button>}
          </div>)}
          <small className="muted">Adding another service below never removes these. Switch between them any time.</small>
        </div>}
        <h3 style={{ margin: "14px 0 4px" }}>Add a service</h3>
        <p>These services sign in with an API key from their dashboard (API or Developers section), not a username and password. The CRM then lists your agents and numbers to choose from.</p>
        <label>Calling service
          <select value={provider} onChange={e => pick(e.target.value)}>
            {providers.map(x => <option key={x.key} value={x.key}>{x.label}{x.key === active ? " — in use now" : x.saved ? " (connected — pasting a key replaces it)" : ""}</option>)}
          </select></label>
        <label>API key
          <input type="password" value={apiKey} onChange={e => { setApiKey(e.target.value); setFound(null); }} autoComplete="off" placeholder="Paste the key from your dashboard" /></label>
        {p?.note && <small className="muted">{p.note}</small>}
        {!found && provider !== "sarvam" && <button className="outline-button" onClick={look} disabled={!apiKey.trim() || !!busy}>
          {busy === "look" ? <><Loader2 size={14} className="spin" /> Connecting…</> : "Connect & find my agents"}</button>}
        {(found || provider === "sarvam") && <>
          {needsAgent && <label>Agent
            <select value={agentId} onChange={e => setAgentId(e.target.value)}>
              <option value="">— Choose an agent —</option>
              {found?.agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></label>}
          {provider === "vapi" && <small className="muted">{found?.agents.length ? `Found ${found.agents.length} assistant${found.agents.length === 1 ? "" : "s"} in your account. ` : ""}The CRM sets up its own assistant in the next step.</small>}
          {found?.numbers.length > 0 && <label>Caller number (optional)
            <select value={number} onChange={e => setNumber(e.target.value)}>
              <option value="">— Automatic —</option>
              {found.numbers.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select></label>}
          {provider === "sarvam" && <>
            {p.manual.map(([k, label]) => <label key={k}>{label}<input value={manual[k] || ""} onChange={e => setManual({ ...manual, [k]: e.target.value })} /></label>)}
            <label>Caller number (required)<input value={number} onChange={e => setNumber(e.target.value)} placeholder="The number in that connection" /></label>
          </>}
          <label className="verify-toggle"><input type="checkbox" checked={makeActive} onChange={e => setMakeActive(e.target.checked)} /><span>Use it for calls now (untick to just add it)</span></label>
          <button className="primary-button" onClick={save} disabled={!canSave || !!busy}>{busy === "save" ? <Loader2 size={14} className="spin" /> : "Connect"}</button>
        </>}
      </>}
    </div>
  </div>;
}
